import { useRef, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Lock,
  X,
  CircleCheck,
  Clock,
  FileText,
  Camera,
  Upload,
  Wrench,
  Hash,
  User,
  Phone,
  Package,
  ChevronDown,
} from "lucide-react";
import ApiService from "../../../components/Api_services.jsx";
import RegisteredReceived from "../registeredreceived/RegisteredReceived.js";
import MultiPurpose from "../aftersales_multipurpose/MultiPurpose.js";
import { useLanguage } from "../LanguageContext.js";
import {
  getAfterSalesApiSerial,
  getAfterSalesSerialType,
} from "../serialTypeUtils";

const RepairService = ({ onClose, verifyCode, ticketInfo }) => {
  const { trans } = useLanguage();
  const [searchParams] = useSearchParams();
  const sn = atob(searchParams.get("sn") || "");
  const serialNoForApi = getAfterSalesApiSerial(searchParams);
  const serialType = getAfterSalesSerialType(searchParams);

  const [jobStatus, setJobStatus] = useState("");
  const [revisitInput, setRevisitInput] = useState("");
  const [completedInput, setCompletedInput] = useState("");
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [uploadedPartPhoto, setUploadedPartPhoto] = useState([]);
  const photoInputRef = useRef(null);
  const partPhotoInputRef = useRef(null);
  const [isChangeParts, setIsChangeParts] = useState("");
  const [showRepairReason, setShowRepairReason] = useState(false);
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [serviceReasonList, setServiceReasonList] = useState([]);
  const [showRegistered, setShowRegistered] = useState(false);
  const [repairStatus, setRepairStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    jobstatus: "",
    remark: "",
    photo: "",
    partreplaced: "",
    partphoto: "",
    servreason: "",
  });
  const [notifMessage, setNotifMessage] = useState("");
  const [showMessage, setShowMessage] = useState(false);

  useEffect(() => {
    const fetchRepairRegisterStatus = async () => {
      try {
        const response = await ApiService.checkRepairRegisterStatus(
          ticketInfo?.ticket_no,
        );

        if (response) {
          console.log("response", response);
          if (response.status === 200) {
            setRepairStatus("completed");
            setShowRegistered(true);
          } else if (
            response.message ===
            "Ticket is not eligible for technician detail registration. Required status: Assigned (3) or Reopen (6)."
          ) {
            setShowMessage(true);
            setNotifMessage(
              "Ticket is not eligible for technician detail registration. Please contact customer support.",
            );
          }
        }
      } catch (error) {
        console.error("Error fetching repair register status:", error);
      }
    };

    fetchRepairRegisterStatus();
  }, [ticketInfo]);

  useEffect(() => {
    const getServiceReason = async () => {
      try {
        const response = await ApiService.getServiceReason(
          serialNoForApi,
          serialType,
        );

        if (response && response.status === 200) {
          setServiceReasonList(response.data);
        }
      } catch (error) {
        console.error("Error fetching warranty info:", error);
      }
    };

    getServiceReason();
  }, [serialNoForApi, serialType]);

  const handleSaveDetail = async () => {
    if (loading) return;
    setLoading(true);
    setNotifMessage("");

    let newErrors = {
      jobstatus: "",
      remark: "",
      photo: "",
      partreplaced: "",
      partphoto: "",
      servreason: "",
    };
    let hasError = false;

    if (!verifyCode) {
      setNotifMessage("Verify code is missing");
      setShowMessage(true);
      setLoading(false);
      return;
    }
    if (!sn) {
      setNotifMessage("Serial no is missing");
      setShowMessage(true);
      setLoading(false);
      return;
    }
    if (!jobStatus) {
      newErrors.jobstatus = "Please select job status";
      hasError = true;
    }
    if (jobStatus === "revisit" && !revisitInput) {
      newErrors.remark = "Please fill in remark";
      hasError = true;
    }
    if (uploadedPhotos.length === 0) {
      newErrors.photo = "Please upload at least one photo";
      hasError = true;
    }
    if (jobStatus === "completed" && isChangeParts === "") {
      newErrors.partreplaced = "Please select were any parts replaced";
      hasError = true;
    }
    if (isChangeParts === true && uploadedPartPhoto.length === 0) {
      newErrors.partphoto = "Please upload part photo";
      hasError = true;
    }
    if (jobStatus === "completed" && selectedReasons.length === 0) {
      newErrors.servreason = "Please select at least one reason";
      hasError = true;
    }

    setErrors(newErrors);

    if (hasError) {
      setLoading(false);
      return;
    }

    const form = new FormData();
    form.append("verify_code", verifyCode || "");
    form.append("serial_no", sn || "");
    const remark = jobStatus === "revisit" ? revisitInput : completedInput;
    form.append("remark", remark || "");
    form.append("job_status", jobStatus === "completed" ? 0 : 1);
    form.append(
      "service_reason",
      selectedReasons.map((reason) => reason.id).join("_"),
    );
    form.append("inserted_by", localStorage.getItem("user_id") || 0);

    if (uploadedPhotos.length > 0) {
      uploadedPhotos.forEach((item) => {
        if (item.file) {
          form.append("photo[]", item.file);
        }
      });
    }

    if (
      isChangeParts === true &&
      uploadedPartPhoto.length > 0 &&
      uploadedPartPhoto[0].file
    ) {
      form.append("part_photo", uploadedPartPhoto[0].file);
    }

    try {
      const res = await ApiService.saveTechnicianDetail(form);
      if (res && (res.status === 200 || res.status === "200")) {
        if (jobStatus === "completed") {
          setRepairStatus("completed");
          setShowRegistered(true);
        } else {
          setRepairStatus("revisit");
          setShowRegistered(true);
        }
      } else {
        const errorMessage = res?.message || "Failed to save technician detail";
        setNotifMessage(errorMessage);
        setShowMessage(true);
      }
    } catch (err) {
      console.error("saveTechnicianDetail error", err);
      setNotifMessage("Failed to save technician detail");
      setShowMessage(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setRevisitInput("");
    setCompletedInput("");
    setUploadedPhotos([]);
    setUploadedPartPhoto([]);
    setIsChangeParts("");
    setSelectedReasons([]);
  }, [jobStatus]);

  const clearError = (field) => setErrors((prev) => ({ ...prev, [field]: "" }));

  return (
    <div className="as-repair-serv-job-modal">
      <div className="as-repair-serv-job-content">
        <div className="as-repair-serv-job-header">
          <div className="as-repair-serv-job-header-title-container">
            <div className="as-repair-serv-job-header-title">
              <Wrench size={16} />
              {trans.repair_and_serv}
            </div>
            <div className="as-repair-serv-job-close" onClick={() => onClose()}>
              <X size={16} />
            </div>
          </div>

          <div className="as-repair-serv-job-header-card">
            <div className="as-repair-serv-job-header-icon">
              <Hash size={16} />
            </div>
            <div>
              <div>{trans.repair_serv_job_id}</div>
              <div className="as-repair-serv-job-header-info">
                {ticketInfo?.ticket_no}
              </div>
            </div>
          </div>
          <div className="as-repair-serv-job-header-card">
            <div className="as-repair-serv-job-header-icon">
              <User size={16} />
            </div>
            <div>
              <div>{trans.repair_serv_cust}</div>
              <div className="as-repair-serv-job-header-info">
                {ticketInfo?.customer_name}
              </div>
            </div>
          </div>
          <div className="as-repair-serv-job-header-card">
            <div className="as-repair-serv-job-header-icon">
              <Phone size={16} />
            </div>
            <div>
              <div>{trans.repair_serv_contact}</div>
              <div className="as-repair-serv-job-header-info">
                {ticketInfo?.customer_phone}
              </div>
            </div>
          </div>
        </div>

        <div className="as-repair-serv-job-main">
          <div className="as-repair-serv-job-card">
            {trans.repair_serv_job_status}
            <div className="as-repair-serv-job-status">
              <div
                className={`as-repair-serv-job-status-item as-green ${jobStatus === "completed" ? "as-active" : ""}`}
                onClick={() => {
                  setJobStatus("completed");
                  clearError("jobstatus");
                }}
              >
                <div className="as-repair-serv-job-status-icon">
                  <CircleCheck size={20} />
                </div>
                {trans.repair_serv_job_status_sub_1}
              </div>
              <div
                className={`as-repair-serv-job-status-item as-orange ${jobStatus === "revisit" ? "as-active" : ""}`}
                onClick={() => {
                  setJobStatus("revisit");
                  clearError("jobstatus");
                }}
              >
                <div className="as-repair-serv-job-status-icon">
                  <Clock size={20} />
                </div>
                {trans.repair_serv_job_status_sub_2}
              </div>
            </div>
            {errors.jobstatus && (
              <div className="as-error">{errors.jobstatus}</div>
            )}
          </div>

          {jobStatus === "revisit" ? (
            <div className="as-repair-serv-job-card">
              <div className="as-repair-serv-job-remark-container">
                <div className="as-repair-serv-job-remark">
                  <FileText size={16} />
                  {trans.repair_serv_remark}
                  <span style={{ color: "red" }}>*</span>
                </div>
                <textarea
                  className="as-repair-serv-job-remark-textarea"
                  placeholder="Describe what needs to be done on the next visit..."
                  value={revisitInput}
                  onChange={(e) => {
                    setRevisitInput(e.target.value);
                    clearError("remark");
                  }}
                  rows={4}
                />
                {errors.remark && (
                  <div className="as-error">{errors.remark}</div>
                )}
              </div>

              <div>
                <div className="as-repair-serv-job-photos">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Camera size={16} />
                    {trans.repair_serv_photos}
                    <span style={{ color: "red" }}>*</span>
                  </div>
                  <span className="as-repair-serv-job-photos-count">
                    {uploadedPhotos.length}/3
                  </span>
                </div>
                <div className="as-repair-serv-job-photos-slots">
                  {uploadedPhotos.map((item, idx) => (
                    <div key={idx} className="as-repair-serv-job-photo-thumb">
                      <img src={item.src} alt={`upload-${idx}`} />
                      <button
                        className="as-repair-serv-job-photo-remove"
                        onClick={() =>
                          setUploadedPhotos((prev) =>
                            prev.filter((_, i) => i !== idx),
                          )
                        }
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                  {uploadedPhotos.length < 3 && (
                    <div
                      className="as-repair-serv-job-photos-add"
                      onClick={() => photoInputRef.current?.click()}
                    >
                      <Upload size={20} color={"#99a1af"} />
                      <span>{trans.add}</span>
                    </div>
                  )}
                </div>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const files = Array.from(e.target.files);
                    const remaining = 3 - uploadedPhotos.length;
                    files.slice(0, remaining).forEach((file) => {
                      const reader = new FileReader();
                      reader.onload = (ev) => {
                        setUploadedPhotos((prev) => [
                          ...prev,
                          { file, src: ev.target.result },
                        ]);
                        clearError("photo");
                      };
                      reader.readAsDataURL(file);
                    });
                    e.target.value = "";
                  }}
                />
                {errors.photo && <div className="as-error">{errors.photo}</div>}
              </div>
            </div>
          ) : jobStatus === "completed" ? (
            <>
              <div className="as-repair-serv-job-card">
                <div>
                  <div className="as-repair-serv-job-changeparts">
                    <Package size={16} />
                    {trans.repair_serv_parts_replaced}
                    <span style={{ color: "red" }}>*</span>
                  </div>

                  <div style={{ display: "flex", gap: "12px" }}>
                    <div
                      className={`as-repair-serv-job-changeparts-btn ${isChangeParts === true ? "as-active" : ""}`}
                      onClick={() => {
                        setIsChangeParts(true);
                        clearError("partreplaced");
                      }}
                    >
                      <CircleCheck size={16} />
                      {trans.yes}
                    </div>
                    <div
                      className={`as-repair-serv-job-changeparts-btn ${isChangeParts === false && isChangeParts !== "" ? "as-active" : ""}`}
                      onClick={() => {
                        setIsChangeParts(false);
                        setUploadedPartPhoto([]);
                        clearError("partreplaced");
                      }}
                    >
                      <X size={16} />
                      {trans.no}
                    </div>
                  </div>
                  {errors.partreplaced && (
                    <div className="as-error">{errors.partreplaced}</div>
                  )}
                </div>

                {isChangeParts && (
                  <div className="as-repair-serv-job-changeparts-container">
                    <div className="as-repair-serv-job-changeparts">
                      <Camera size={16} />
                      {trans.repair_serv_part_photo}
                      <span style={{ color: "red" }}>*</span>
                    </div>
                    {uploadedPartPhoto[0] && (
                      <div className="as-repair-serv-job-changeparts-photo-thumb">
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <CircleCheck size={20} />
                          <span>{uploadedPartPhoto[0].file?.name}</span>
                        </div>
                        <button
                          className="as-repair-serv-job-changeparts-photo-remove"
                          onClick={() => setUploadedPartPhoto([])}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    )}
                    {uploadedPartPhoto.length < 1 && (
                      <div
                        className="as-repair-serv-job-changeparts-photos-add"
                        onClick={() => partPhotoInputRef.current?.click()}
                      >
                        <Upload size={16} color={"#99a1af"} />
                        {trans.repair_serv_part_photo_sub}
                      </div>
                    )}
                    <input
                      ref={partPhotoInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        const reader = new FileReader();
                        reader.onload = (ev) => {
                          setUploadedPartPhoto([
                            { file, src: ev.target.result },
                          ]);
                          clearError("partphoto");
                        };
                        reader.readAsDataURL(file);
                        e.target.value = "";
                      }}
                    />
                    {errors.partphoto && (
                      <div className="as-error">{errors.partphoto}</div>
                    )}
                  </div>
                )}
              </div>

              {isChangeParts !== "" && (
                <div
                  className="as-repair-serv-job-card"
                  style={{ cursor: "pointer" }}
                  onClick={() => setShowRepairReason(true)}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div
                      className="as-repair-serv-job-reason"
                      style={{ margin: "0" }}
                    >
                      <Wrench size={16} />
                      {trans.repair_serv_reason}
                      <span style={{ color: "red" }}>*</span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      {selectedReasons.length > 0 && (
                        <span className="as-repair-serv-job-reason-badge">
                          {selectedReasons.length}{" "}
                          {trans.repair_serv_reason_sub_1}
                        </span>
                      )}
                      {selectedReasons.length === 0 && (
                        <div className="as-repair-serv-job-select">
                          {trans.repair_serv_reason_sub_2}
                        </div>
                      )}
                      <ChevronDown size={16} />
                    </div>
                  </div>

                  {selectedReasons.length > 0 && (
                    <div className="as-repair-serv-job-reason-tags">
                      {selectedReasons.map((reason) => (
                        <span
                          key={reason.id}
                          className="as-repair-serv-job-reason-tag"
                        >
                          {reason.description}
                        </span>
                      ))}
                    </div>
                  )}
                  {errors.servreason && (
                    <div className="as-error">{errors.servreason}</div>
                  )}
                </div>
              )}

              <div className="as-repair-serv-job-card">
                <div className="as-repair-serv-job-remark-container">
                  <div className="as-repair-serv-job-remark">
                    <FileText size={16} />
                    {trans.repair_serv_remark_optional}
                  </div>
                  <textarea
                    className="as-repair-serv-job-remark-textarea"
                    placeholder="Add any additional notes..."
                    value={completedInput}
                    onChange={(e) => setCompletedInput(e.target.value)}
                    rows={4}
                  />
                </div>

                <div>
                  <div className="as-repair-serv-job-photos">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <Camera size={16} />
                      {trans.repair_serv_photos}
                      <span style={{ color: "red" }}>*</span>
                    </div>
                    <span className="as-repair-serv-job-photos-count">
                      {uploadedPhotos.length}/3
                    </span>
                  </div>
                  <div className="as-repair-serv-job-photos-slots">
                    {uploadedPhotos.map((item, idx) => (
                      <div key={idx} className="as-repair-serv-job-photo-thumb">
                        <img src={item.src} alt={`upload-${idx}`} />
                        <button
                          className="as-repair-serv-job-photo-remove"
                          onClick={() =>
                            setUploadedPhotos((prev) =>
                              prev.filter((_, i) => i !== idx),
                            )
                          }
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                    {uploadedPhotos.length < 3 && (
                      <div
                        className="as-repair-serv-job-photos-add"
                        onClick={() => photoInputRef.current?.click()}
                      >
                        <Upload size={20} color={"#99a1af"} />
                        <span>{trans.add}</span>
                      </div>
                    )}
                  </div>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      const remaining = 3 - uploadedPhotos.length;
                      files.slice(0, remaining).forEach((file) => {
                        const reader = new FileReader();
                        reader.onload = (ev) => {
                          setUploadedPhotos((prev) => [
                            ...prev,
                            { file, src: ev.target.result },
                          ]);
                          clearError("photo");
                        };
                        reader.readAsDataURL(file);
                      });
                      e.target.value = "";
                    }}
                  />
                  {errors.photo && (
                    <div className="as-error">{errors.photo}</div>
                  )}
                </div>
              </div>
            </>
          ) : null}

          <button
            onClick={handleSaveDetail}
            className={`as-repair-serv-button ${
              jobStatus === "completed" &&
              (isChangeParts === true ? uploadedPartPhoto.length > 0 : true) &&
              selectedReasons.length > 0 &&
              uploadedPhotos.length > 0
                ? "as-green"
                : jobStatus === "revisit" &&
                    revisitInput &&
                    uploadedPhotos.length > 0
                  ? "as-orange"
                  : ""
            }`}
          >
            {jobStatus === "revisit" ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                <Clock size={20} />
                {trans.repair_serv_button_1}
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                <CircleCheck size={20} />
                {trans.repair_serv_button_2}
              </div>
            )}
          </button>
        </div>
      </div>

      {showRepairReason && (
        <div className="as-repair-serv-repair-reason-modal">
          <div className="as-repair-serv-repair-reason-content">
            <div className="as-repair-serv-repair-reason-header">
              <div>
                <div className="as-repair-serv-repair-reason-title">
                  {trans.repair_serv_reason}
                </div>
                <div className="as-repair-serv-repair-reason-subtitle">
                  {trans.repair_serv_reason_sub_3}
                </div>
              </div>
              <div
                className="as-repair-serv-repair-reason-close"
                onClick={() => setShowRepairReason(false)}
              >
                <X size={16} />
              </div>
            </div>

            <div className="as-repair-serv-repair-reason-list">
              {serviceReasonList.map((reason) => {
                const isSelected = selectedReasons.some(
                  (r) => r.id === reason.id,
                );
                return (
                  <div
                    key={reason.id}
                    className={`as-repair-serv-repair-reason-item ${
                      isSelected ? "as-selected" : ""
                    }`}
                    onClick={() => {
                      setSelectedReasons((prev) =>
                        prev.some((r) => r.id === reason.id)
                          ? prev.filter((r) => r.id !== reason.id)
                          : [...prev, reason],
                      );
                      clearError("servreason");
                    }}
                  >
                    <span>{reason.description}</span>
                    <div
                      className={`as-repair-serv-repair-reason-radio ${
                        isSelected ? "as-selected" : ""
                      }`}
                    >
                      {isSelected && (
                        <div className="as-repair-serv-repair-reason-radio-dot" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="as-repair-serv-repair-reason-footer">
              <button
                className="as-repair-serv-repair-reason-done"
                onClick={() => setShowRepairReason(false)}
              >
                <CircleCheck size={16} />
                {trans.done}
                {selectedReasons.length > 0 &&
                  `(${selectedReasons.length} selected)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {showMessage && (
        <MultiPurpose
          title="Registration Status"
          message={notifMessage}
          yesText="OK"
          hideNo={true}
          onYes={() => {
            setShowMessage(false);
            window.location.reload();
          }}
          onClose={() => {
            setShowMessage(false);
            window.location.reload();
          }}
        />
      )}

      {showRegistered && (
        <RegisteredReceived
          isRepairCompleted={repairStatus === "completed"}
          isRepairRevisit={repairStatus === "revisit"}
        />
      )}
    </div>
  );
};

export default RepairService;
